import clsx from "clsx";
import { memo, useMemo } from "react";
import Icon from "../Icon/Icon";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import {
  formatFileSize,
  getFileTypeByFilename,
  getUniqueImageMimeTypes,
  rgbaObjectToString,
} from "src/helpers/utils";
import useColors from "src/hooks/useColors";
import SmartImage from "../SmartImage/SmartImage";
import { ICON_SIZES } from "src/constants/icons";
import { IButton } from "../Button/Button.types";

interface IFileCard {
  name: string;
  size: number;
  thumbnail?: string;
  type: string;
  buttons?: IButton[];
  onClick: () => void;
}

const FileCard: React.FC<IFileCard> = ({
  name,
  size = 0,
  thumbnail,
  type,
  buttons = [],
  onClick,
}) => {
  const { colors } = useColors();

  const fileInfo = useMemo(() => {
    return getFileTypeByFilename(name);
  }, [name]);

  const mimesWithPreview = getUniqueImageMimeTypes();

  return (
    <div
      className={clsx([
        "flex",
        "w-full",
        "group",
        "flex-row",
        "gap-2",
        "items-center",
        // "bg-gray-50/50 dark:bg-gray-900/50",
        "p-2",
        "md:p-2",
        "md:gap-2",
        "gap-2",
        "cursor-pointer",
        "rounded",
        "border",
        "border-gray-50",
        "dark:border-gray-900/50",
      ])}
      onClick={() => {
        onClick?.();
      }}
    >
      <div
        className={clsx([
          "inline-flex",
          "h-[40px]",
          "w-[40px]",
          "rounded",
          "bg-checkers",
          "flex-shrink-0",
          "items-center",
          "justify-center",
          "overflow-hidden",
        ])}
        style={{
          background: rgbaObjectToString(
            colors.files[fileInfo.id]?.bg || { r: 0, g: 0, b: 0, a: 1 },
          ),
          color: rgbaObjectToString(
            colors.files[fileInfo.id]?.color || {
              r: 255,
              g: 255,
              b: 255,
              a: 1,
            },
          ),
        }}
      >
        {thumbnail && mimesWithPreview.includes(type) ? (
          <SmartImage
            src={thumbnail}
            alt={name}
            className={clsx(["h-full", "w-full", "object-cover"])}
          />
        ) : (
          <Icon name={fileInfo.icon as any} size={ICON_SIZES.SM} />
        )}
      </div>
      <div className={clsx(["flex", "w-full", "truncate", "flex-col"])}>
        <div className={clsx(["truncate", "text-xs"])}>{name}</div>
        <div className={clsx(["truncate", "text-xs"])}>
          {formatFileSize(size)}
        </div>
      </div>
      {buttons?.length > 0 && (
        <div className={clsx(["inline-flex", "flex-shrink-0"])}>
          <ButtonGroup buttons={buttons} />
        </div>
      )}
    </div>
  );
};

export default memo(FileCard);
